import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EnvelopeIcon, LockClosedIcon, UserIcon, BuildingOfficeIcon, CheckIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const RegisterBusiness = () => {
    const [companyName, setCompanyName] = useState('');
    const [contactName, setContactName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { checkAuth } = useAuth();

    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    // Применить или оплатить тариф после регистрации B2B
    const applyPendingTariff = async (token) => {
        const pendingTariffStr = localStorage.getItem('pending_tariff');
        if (!pendingTariffStr) return null;
        
        try {
            const tariff = JSON.parse(pendingTariffStr);
            localStorage.removeItem('pending_tariff');
            
            if (tariff.price_per_month === 0) {
                // Бесплатный тариф - применяем через API организации
                const res = await fetch('/api/admin/my-organization/tariff', {
                    method: 'PUT',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ tariff_id: tariff.id })
                });
                if (res.ok) {
                    toast.success('Тариф активирован!');
                }
                return { redirect: '/manager' };
            } else {
                // Платный тариф - создаём платёж для организации
                const res = await fetch('/api/payments/create', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                        amount: tariff.price_per_month, 
                        tariff_id: tariff.id, 
                        for_organization: true,
                        return_url: window.location.origin + '/manager' 
                    })
                });
                const data = await res.json();
                if (data.confirmation_url) {
                    return { paymentUrl: data.confirmation_url };
                }
            }
        } catch (e) {
            console.error('Error applying tariff:', e);
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!companyName || !contactName || !email || !password || !confirmPassword) {
            setError('Все поля обязательны для заполнения');
            return;
        }
        if (contactName.length < 2) {
            setError('ФИО должно содержать минимум 2 символа');
            return;
        }
        if (companyName.length < 2) {
            setError('Название компании должно содержать минимум 2 символа');
            return;
        }
        if (!validateEmail(email)) {
            setError('Пожалуйста, введите корректный email адрес');
            return;
        }
        if (password.length < 6) {
            setError('Пароль должен содержать минимум 6 символов');
            return;
        }
        if (password !== confirmPassword) {
            setError('Пароли не совпадают');
            return;
        }

        setLoading(true);
        try {
            const res = await fetch('/api/auth/register/business', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ company_name: companyName, contact_name: contactName, email, password })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Ошибка регистрации');
            
            // Сохраняем токены
            localStorage.setItem('access_token', data.access_token);
            localStorage.setItem('refresh_token', data.refresh_token);
            
            // Проверяем и применяем отложенный тариф
            const tariffResult = await applyPendingTariff(data.access_token);
            
            // Обновляем контекст авторизации (загружаем user/limits)
            await checkAuth();
            
            if (tariffResult?.paymentUrl) {
                // Переход на оплату
                window.location.href = tariffResult.paymentUrl;
                return;
            }
            
            toast.success('Регистрация успешна!');
            navigate(tariffResult?.redirect || '/manager');
        } catch (err) {
            setError(err.message || 'Произошла ошибка при регистрации');
        } finally {
            setLoading(false);
        }
    };

    const features = [
        'Управление командой сотрудников',
        'Корпоративные тарифы',
        'Статистика по организации',
        'Собственные сценарии обучения'
    ];

    return (
        <div className="min-h-screen flex">
            {/* Левая часть - форма */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-gray-50 dark:bg-gray-900">
                <div className="w-full max-w-md">
                    <div className="lg:hidden flex justify-center mb-8">
                        <img src="/buzilla_universal.svg" alt="Бузилла" className="w-20 h-20" />
                    </div>

                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Регистрация для бизнеса</h2>
                        <p className="text-gray-600 dark:text-gray-400">Создайте аккаунт для вашей компании</p>
                    </div>

                    {error && (
                        <div className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
                            <p className="text-red-600 dark:text-red-400 text-sm text-center">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="relative">
                            <BuildingOfficeIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                required
                                placeholder="Название компании"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                        </div>

                        <div className="relative">
                            <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                required
                                placeholder="ФИО контактного лица"
                                value={contactName}
                                onChange={(e) => setContactName(e.target.value)}
                                className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                        </div>

                        <div className="relative">
                            <EnvelopeIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="email"
                                required
                                placeholder="Email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                        </div>

                        <div className="relative">
                            <LockClosedIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="password"
                                required
                                placeholder="Пароль"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                        </div>

                        <div className="relative">
                            <LockClosedIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="password"
                                required
                                placeholder="Подтвердите пароль"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-6"
                        >
                            {loading ? 'Регистрация...' : 'Создать аккаунт компании'}
                        </button>
                    </form>

                    <p className="mt-8 text-center text-gray-600 dark:text-gray-400">
                        Уже есть аккаунт?{' '}
                        <Link to="/login" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 font-semibold">
                            Войти
                        </Link>
                    </p>
                    <p className="mt-2 text-center text-gray-600 dark:text-gray-400">
                        <Link to="/register" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 text-sm">
                            Регистрация для частных лиц
                        </Link>
                    </p>
                </div>
            </div>

            {/* Правая часть - информация */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 items-center justify-center p-12">
                <div className="max-w-lg text-white">
                    <div className="flex justify-center mb-8">
                        <img src="/buzilla_universal.svg" alt="Бузилла" className="w-24 h-24" />
                    </div>
                    <h1 className="text-4xl font-bold mb-6 text-center">Корпоративное обучение</h1>
                    <p className="text-xl text-blue-100 mb-8 text-center">
                        Тренируйте навыки общения вашей команды с помощью ИИ-симуляций
                    </p>
                    <div className="space-y-4">
                        {features.map((feature, index) => (
                            <div key={index} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                                <div className="flex-shrink-0 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                                    <CheckIcon className="w-5 h-5" />
                                </div>
                                <span className="text-lg">{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegisterBusiness;
